import apiFetch from './client';

export interface Condominium {
  id: string;
  name: string;
  slug: string;
  createdAt: string;
  updatedAt: string;
}

export interface CondominiumWithMemberCount extends Condominium {
  memberCount: number;
}

export interface CondominiumFormPayload {
  name: string;
  slug: string;
}

export async function listCondominiumsWithMemberCount(): Promise<CondominiumWithMemberCount[]> {
  const data = await apiFetch<CondominiumWithMemberCount[]>('/condominiums/list/with-member-count');
  return data ?? [];
}

export async function listManagedCondominiumsWithMemberCount(): Promise<
  CondominiumWithMemberCount[]
> {
  const data = await apiFetch<CondominiumWithMemberCount[]>(
    '/condominiums/managed/with-member-count',
  );
  return data ?? [];
}

export async function createCondominium(payload: CondominiumFormPayload): Promise<Condominium> {
  return apiFetch<Condominium>('/condominiums', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateCondominium(
  id: string,
  payload: CondominiumFormPayload,
): Promise<Condominium> {
  return apiFetch<Condominium>(`/condominiums/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

// --- Detalhes do condomínio (blocos, apartamentos, vagas) ---

export type ParkingSpotStatus = 'OCCUPIED' | 'AVAILABLE' | 'BORROWED' | 'RENTED';
export type RentStatusMode = 'MARK_AS_RENTED' | 'KEEP_OCCUPIED';

export interface ParkingSpot {
  id: string;
  code: string;
  location?: string;
  notes?: string;
  level?: string;
  status: ParkingSpotStatus;
  rentStatusMode: RentStatusMode;
  isActive: boolean;
  ownerCount?: number;
  apartmentId?: string;
  locationX?: number;
  locationY?: number;
  locationWidth?: number;
  locationHeight?: number;
}

export interface Apartment {
  id: string;
  number: string;
  floor?: string;
  isActive: boolean;
  memberCount?: number;
}

export interface Block {
  id: string;
  name: string;
  code?: string;
  isActive: boolean;
  parkingSpots: ParkingSpot[];
  apartments: Apartment[];
}

export interface CondominiumDetails {
  id: string;
  name: string;
  slug: string;
  blocks: Block[];
}

export interface BlockFormPayload {
  name: string;
  code?: string;
  isActive?: boolean;
}

export interface ApartmentFormPayload {
  number: string;
  floor?: string;
  isActive?: boolean;
}

export interface ParkingSpotFormPayload {
  code: string;
  location?: string;
  notes?: string;
  level?: string;
  status?: ParkingSpotStatus;
  rentStatusMode?: RentStatusMode;
  isActive?: boolean;
  apartmentId?: string;
  locationX?: number | null;
  locationY?: number | null;
  locationWidth?: number | null;
  locationHeight?: number | null;
}

export interface ApartmentMember {
  id: string;
  userId: string;
  userEmail: string;
  userName: string;
  apartmentId: string;
  apartmentNumber: string;
  role: 'MANAGER' | 'USER';
  status: string;
  createdAt: string;
}

export interface AssociateMemberPayload {
  userId: string;
}

export async function getCondominiumDetails(id: string): Promise<CondominiumDetails> {
  return apiFetch<CondominiumDetails>(`/condominiums/${id}/details`);
}

export async function createBlock(
  condominiumId: string,
  payload: BlockFormPayload,
): Promise<Block> {
  return apiFetch<Block>(`/condominiums/${condominiumId}/blocks`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateBlock(
  condominiumId: string,
  blockId: string,
  payload: BlockFormPayload,
): Promise<Block> {
  return apiFetch<Block>(`/condominiums/${condominiumId}/blocks/${blockId}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function listApartmentsByBlock(blockId: string): Promise<Apartment[]> {
  const data = await apiFetch<Apartment[]>(`/blocks/${blockId}/apartments`);
  return data ?? [];
}

export async function createApartment(
  blockId: string,
  payload: ApartmentFormPayload,
): Promise<Apartment> {
  return apiFetch<Apartment>(`/blocks/${blockId}/apartments`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateApartment(
  blockId: string,
  apartmentId: string,
  payload: ApartmentFormPayload,
): Promise<Apartment> {
  return apiFetch<Apartment>(`/blocks/${blockId}/apartments/${apartmentId}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function createParkingSpot(
  blockId: string,
  payload: ParkingSpotFormPayload,
): Promise<ParkingSpot> {
  return apiFetch<ParkingSpot>(`/blocks/${blockId}/parking-spots`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateParkingSpot(
  blockId: string,
  spotId: string,
  payload: ParkingSpotFormPayload,
): Promise<ParkingSpot> {
  return apiFetch<ParkingSpot>(`/blocks/${blockId}/parking-spots/${spotId}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function listApartmentMembers(
  blockId: string,
  apartmentId: string,
): Promise<ApartmentMember[]> {
  const data = await apiFetch<ApartmentMember[]>(
    `/blocks/${blockId}/apartments/${apartmentId}/members`,
  );
  return data ?? [];
}

export async function associateApartmentMember(
  blockId: string,
  apartmentId: string,
  payload: AssociateMemberPayload,
): Promise<ApartmentMember> {
  return apiFetch<ApartmentMember>(`/blocks/${blockId}/apartments/${apartmentId}/members`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function disassociateApartmentMember(
  blockId: string,
  apartmentId: string,
  userId: string,
): Promise<void> {
  await apiFetch<void>(
    `/blocks/${blockId}/apartments/${apartmentId}/members/users/${userId}`,
    { method: 'DELETE' },
  );
}
